import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg, { getOrganizationSchema, getWebsiteSchema, getFAQSchema } from '@/components/feature/SchemaOrg';
import HeroSection from './components/HeroSection';
import VideoCard from './components/VideoCard';
import ArticleCard from './components/ArticleCard';
import { categories } from '@/mocks/categories';
import { getFeaturedVideos, getLatestVideos, getPopularVideos } from '@/mocks/videos';
import { getFeaturedArticles, getLatestArticles } from '@/mocks/articles';
import { seoConfig } from '@/mocks/seo';

export default function Home() {
  const featuredVideos = getFeaturedVideos();
  const popularVideos = getPopularVideos(6);
  const latestVideos = getLatestVideos(8);
  const featuredArticles = getFeaturedArticles();
  const latestArticles = getLatestArticles(4);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SchemaOrg type="Organization" data={getOrganizationSchema()} />
      <SchemaOrg type="WebSite" data={getWebsiteSchema()} />
      <SchemaOrg type="FAQPage" data={getFAQSchema()} />

      <Navbar />

      <main>
        <HeroSection />

        <section id="categories" className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                视频平台分类
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                六大视频平台资源一站聚合，点击进入您喜欢的视频平台
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300"
                >
                  <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl ${cat.color} bg-opacity-10 mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${cat.icon} text-xl md:text-2xl text-white`}></i>
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground-900 mb-1 whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 text-center leading-relaxed">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {featuredVideos.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    精选推荐
                  </h2>
                  <p className="text-sm text-foreground-600">编辑精选的高分影视作品</p>
                </div>
                <Link
                  to="/category/hot"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {featuredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  热门排行
                </h2>
                <p className="text-sm text-foreground-600">全网观众都在看的热门视频</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {popularVideos.map((video, index) => (
                <div key={video.id} className="relative">
                  <div className="absolute top-3 left-3 z-10 w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500 text-white text-sm font-bold">
                    {index + 1}
                  </div>
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-600">持续更新的最新视频内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {latestVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {featuredArticles.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-600">视频行业资讯与技术解读</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>
        )}

        {latestArticles.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-600">更多精彩内容持续更新中</p>
                </div>
              </div>
              <div className="space-y-3 md:space-y-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="px-4 md:px-6 py-12 md:py-16 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20gradient%20with%20soft%20flowing%20shapes%20in%20deep%20red%20and%20dark%20amber%2C%20modern%20streaming%20platform%20background%2C%20elegant%20minimal%20design&width=1800&height=600&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              免费在线观看 · 高清不缓冲
            </h2>
            <p className="text-sm md:text-lg text-white/80 mb-6 max-w-xl mx-auto">
              私密视频聚合红桃视频、西瓜视频、小蓝视频、茄子视频、久久视频、芒果视频六大平台资源，海量高清视频免费观看，全集资源一站畅享。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/hongtao"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium transition-all duration-300 whitespace-nowrap"
              >
                立即免费观看
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3 bg-white/15 hover:bg-white/25 text-white rounded-full font-medium backdrop-blur-sm transition-all duration-300 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}